import { useState, useEffect } from "react";

interface PdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrls: string[]; // Liste des URLs des PDFs à afficher
  title?: string;
}

export function PdfPreviewModal({ isOpen, onClose, pdfUrls, title = "Aperçu des devis" }: PdfPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentUrl = pdfUrls[currentIndex];
  const hasMultiple = pdfUrls.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {hasMultiple && (
              <span className="text-sm text-gray-500">
                ({currentIndex + 1} / {pdfUrls.length})
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Fermer"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation arrows (if multiple PDFs) */}
        {hasMultiple && (
          <div className="flex items-center justify-center gap-4 p-2 bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Précédent
            </button>
            <span className="text-sm text-gray-600">
              Devis {currentIndex + 1} sur {pdfUrls.length}
            </span>
            <button
              onClick={() => setCurrentIndex((prev) => Math.min(pdfUrls.length - 1, prev + 1))}
              disabled={currentIndex === pdfUrls.length - 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Suivant →
            </button>
          </div>
        )}

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          <iframe
            src={currentUrl}
            className="w-full h-full border-0"
            title={`PDF Preview ${currentIndex + 1}`}
          />
        </div>

        {/* Footer with actions */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            🔗 Ouvrir dans un nouvel onglet
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

